import { produce } from 'immer';
import { doAseLoadDataFromNewApi, eventAssetDataUpdated } from 'store/actions';
import * as actions from 'store/assessments/actions';
import {
  Assessment,
  AssessmentState,
  ChecklistItemResult,
  QuestionResult,
  SectionResult,
  Action,
  Control,
  AseTplFieldGroup,
  AseTplFieldField,
  AseTplFieldSection,
  AseFieldInstance,
  ActionSuggestion,
  ControlSuggestion,
} from 'store/assessments/types';
import { RESET_DEV_DATA, SIGN_OUT } from 'store/auth/actions';
import { createReducer } from 'typesafe-actions';
import { File } from 'store/assets/types';
import { AppContextEnum, FieldResult } from 'types/types';
import {
  Answer,
  Category,
  ChecklistItem,
  ComplianceSubtype,
  ComplianceType,
  Matrix,
  Question,
  Section,
  Team,
} from 'store/organisations/types';
import { intersection, sortBy, toInteger, union, uniq } from 'lodash';
import { mapObjectValues } from 'lib/riskbase';
import equal from 'fast-deep-equal';
import {
  translateAction,
  translateControl,
  translateFieldRepeatableResultNullable,
  translateFieldResult,
  translateFieldResultNullable,
} from './helper';
import {
  getFieldGroupRequiredResultsValuesCountFromValues,
  getFieldGroupRequiredValuesCount,
  getFieldInstanceRequiredValuesCount,
  offlineAssetFieldInstanceToAssessment,
} from './utilities';

const initialState: AssessmentState = {
  isOpen: false,
  collapsedSections: {},
};

export const assessmentReducer = createReducer(initialState)
  .handleAction(actions.LOAD_ASSESSMENT.success, (state, action) => ({
    ...state,
    isOpen: true,
    context: {
      context: action.payload.isOnline
        ? AppContextEnum.remoteAssessment
        : AppContextEnum.localAssessment,
      assetId: action.payload.assetId,
      assessmentId: action.payload.id,
    },
    collapsedSections: {},
    assessment: action.payload,
  }))
  .handleAction(actions.CLOSE_ASSESSMENT, (state) => ({
    ...state,
    isOpen: false,
  }))
  .handleAction(actions.UNLOAD_ASSESSMENT, (state) => ({
    ...state,
    assessment: undefined,
  }))
  .handleAction(actions.doAseSetSectionIsCollapsed, (state, action) => ({
    ...state,
    collapsedSections: {
      ...state.collapsedSections,
      [action.payload.sectionId]: action.payload.isCollapsed,
    },
  }))
  .handleAction(actions.doAseMergeTemplateSuggested, (state, action) =>
    produce(state, (draft) => {
      const draftTemplate = draft.assessment?.template;
      if (draftTemplate) {
        if (action.payload.actionSuggestions !== undefined) {
          Object.entries(action.payload.actionSuggestions).forEach(
            ([id, item]) => {
              draftTemplate.actionSuggestions[id] = item;
            },
          );
        }
        if (action.payload.controlSuggestions !== undefined) {
          Object.entries(action.payload.controlSuggestions).forEach(
            ([id, item]) => {
              draftTemplate.controlSuggestions[id] = item;
            },
          );
        }
      }
    }),
  )
  .handleAction(actions.doAseMergeResults, (state, action) =>
    produce(state, (draft) => {
      const draftAssessment = draft.assessment;
      const stateAssessment = state.assessment;
      if (!stateAssessment || !draftAssessment || !action.payload) {
        return;
      }
      const draftStats = draftAssessment.stats;
      const draftResults = draftAssessment.results;
      if (action.payload.asset !== undefined) {
        draftResults.asset = action.payload.asset;
      }
      if (action.payload.details !== undefined) {
        draftResults.details = action.payload.details;
      }
      if (action.payload.sections !== undefined) {
        Object.entries(action.payload.sections).forEach(([id, item]) => {
          draftResults.sections[id] = item;
        });
      }
      if (action.payload.questions !== undefined) {
        Object.entries(action.payload.questions).forEach(([id, item]) => {
          draftResults.questions[id] = item;
        });
      }
      if (action.payload.questionComments !== undefined) {
        Object.entries(action.payload.questionComments).forEach(
          ([id, item]) => {
            draftResults.questionComments[id] = item;
          },
        );
      }
      if (action.payload.checklistItems !== undefined) {
        Object.entries(action.payload.checklistItems).forEach(([id, item]) => {
          draftResults.checklistItems[id] = item;
        });
      }
      if (action.payload.controls !== undefined) {
        Object.entries(action.payload.controls).forEach(([id, item]) => {
          draftResults.controls[id] = item;
        });
      }
      if (action.payload.actions !== undefined) {
        Object.entries(action.payload.actions).forEach(([id, item]) => {
          draftResults.actions[id] = item;
        });
      }
      if (action.payload.fieldInstances !== undefined) {
        Object.entries(action.payload.fieldInstances).forEach(([id, item]) => {
          const fieldInstance = draftResults.fieldInstances[id];
          draftResults.fieldInstances[id] = {
            action: item.action,
            autoId: item.autoId,
            createdTs: item.createdTs,
            groupId: item.groupId,
            instanceId: item.instanceId,
            updatedTs: item.updatedTs,
            checks: item.checks,
            draftAutoId: item.draftAutoId,
            reference: item.reference,
            results: fieldInstance
              ? { ...fieldInstance.results, ...item.results }
              : item.results,
          };
          // Update stats for instance
          const { groupId } = draftResults.fieldInstances[id]!;
          draftStats.fieldInstanceRequiredResultsCount[id] =
            getFieldInstanceRequiredValuesCount({
              assetResults:
                stateAssessment.assetData.fieldInstances[id]?.results || {},
              instance: draftResults.fieldInstances[id]!,
              group: stateAssessment.otherData.fields.groups[groupId]!,
              fields: stateAssessment.otherData.fields.fields,
            });
          draftStats.fieldGroupRequiredResultsCount[groupId] =
            getFieldGroupRequiredResultsValuesCountFromValues({
              existingCount:
                stateAssessment.stats.fieldInstanceRequiredResultsCount[id] ??
                0,
              newCount: draftStats.fieldInstanceRequiredResultsCount[id]!,
              groupCount:
                draftStats.fieldGroupRequiredResultsCount[groupId] ?? 0,
            });
        });
      }
      if (action.payload.fieldInstanceChecks !== undefined) {
        Object.entries(action.payload.fieldInstanceChecks).forEach(
          ([id, item]) => {
            draftResults.fieldInstanceChecks[id] = item;
          },
        );
      }
      if (action.payload.fieldResults !== undefined) {
        Object.entries(action.payload.fieldResults).forEach(([id, item]) => {
          draftResults.fieldResults[id] = item;
        });
        // Update stats for group
        const affectedFieldIds = Object.keys(action.payload.fieldResults);
        Object.values(stateAssessment.otherData.fields.groups)
          .filter(
            (group) =>
              !!stateAssessment.template.fields.groups[group.id] &&
              !group.repeatable &&
              !!intersection(affectedFieldIds, group.fieldIds).length,
          )
          .forEach((group) => {
            draftStats.fieldGroupRequiredResultsCount[group.id] =
              getFieldGroupRequiredResultsValuesCountFromValues({
                existingCount:
                  stateAssessment.stats.fieldGroupRequiredResultsCount[
                    group.id
                  ] ?? 0,
                newCount: getFieldGroupRequiredValuesCount({
                  assetResults: draftAssessment.assetData.fieldResults,
                  results: draftResults.fieldResults,
                  group,
                  fields: stateAssessment.otherData.fields.fields,
                }),
                groupCount:
                  draftStats.fieldGroupRequiredResultsCount[group.id] ?? 0,
              });
          });
      }
      if (action.payload.signatories !== undefined) {
        Object.entries(action.payload.signatories).forEach(([id, item]) => {
          draftResults.signatories[id] = item;
        });
      }
    }),
  )
  .handleAction(actions.doAseSetGroupFilter, (state, action) =>
    state.assessment
      ? {
          ...state,
          assessment: {
            ...state.assessment,
            filterGroups: {
              ...state.assessment.filterGroups,
              [action.payload.groupId]: action.payload.data,
            },
          },
        }
      : state,
  )
  .handleAction(eventAssetDataUpdated, (state, action) =>
    produce(state, (draft) => {
      const stateAssessment = state.assessment;
      const draftAssessment = draft.assessment;
      if (
        draftAssessment &&
        stateAssessment &&
        state.context &&
        state.context.context === AppContextEnum.localAssessment &&
        state.context.assetId === action.payload.assetId
      ) {
        const affectedFieldIds: string[] = [];
        const affectedFieldInstanceIds: string[] = [];

        if (action.payload.updatedKeys.includes('plans')) {
          draftAssessment.assetData.plans = Object.entries(
            action.payload.data.plans,
          ).reduce<Assessment['assetData']['plans']>((acc, [planId, plan]) => {
            if (plan) {
              acc[planId] = {
                id: plan.id,
                name: plan.name,
                fileId: plan.fileId,
                fileType: plan.fileType || 'image',
                filePage: plan.filePage || 0,
                fileWidth: plan.fileWidth,
                fileHeight: plan.fileHeight,
                pinCount: plan.pinCount,
              };
            }
            return acc;
          }, {});
        }

        if (action.payload.updatedKeys.includes('files')) {
          draftAssessment.assetData.files = Object.values(
            action.payload.data.files,
          ).reduce<Record<string, File>>((acc, file) => {
            if (file) {
              acc[file.id] = file;
            }
            return acc;
          }, {});
        }

        if (action.payload.updatedKeys.includes('fieldResults')) {
          Object.values(action.payload.data.fieldResults).forEach(
            (fieldResult) => {
              if (
                !equal(
                  stateAssessment.assetData.fieldResults[fieldResult.fieldId],
                  fieldResult,
                )
              ) {
                affectedFieldIds.push(fieldResult.fieldId);
                draftAssessment.assetData.fieldResults[fieldResult.fieldId] =
                  fieldResult;
              }
            },
          );
        }

        if (action.payload.updatedKeys.includes('fieldInstances')) {
          Object.values(action.payload.data.fieldInstances).forEach(
            (rawFieldInstance) => {
              const fieldInstance =
                offlineAssetFieldInstanceToAssessment(rawFieldInstance);
              if (
                !equal(
                  stateAssessment.results.fieldInstances[
                    fieldInstance.instanceId
                  ],
                  fieldInstance,
                )
              ) {
                affectedFieldInstanceIds.push(fieldInstance.instanceId);
                draftAssessment.assetData.fieldInstances[
                  fieldInstance.instanceId
                ] = fieldInstance;
              }
            },
          );
        }

        if (action.payload.updatedKeys.includes('fieldInstanceAutoIds')) {
          draftAssessment.assetData.fieldInstanceAutoIds =
            action.payload.data.fieldInstanceAutoIds;
        }
        if (action.payload.updatedKeys.includes('defaultInstanceChecks')) {
          draftAssessment.assetData.defaultInstanceChecks =
            action.payload.data.defaultInstanceChecks || {};
        }
        if (action.payload.route === 'sync') {
          if (action.payload.updatedKeys.includes('assessments')) {
            const assessment =
              action.payload.data.assessments[draftAssessment.id];
            if (assessment?.editable) {
              if (!draftAssessment.results.reference && assessment.reference) {
                draftAssessment.results.reference = assessment.reference;
              }

              // Field Results - merge into assessment
              union(
                Object.keys(assessment.fieldResults),
                Object.keys(stateAssessment.results.fieldResults),
              ).forEach((fieldId) => {
                const fieldResult = assessment.fieldResults[fieldId];
                if (
                  !equal(
                    stateAssessment.results.fieldResults[fieldId],
                    fieldResult,
                  )
                ) {
                  affectedFieldIds.push(fieldId);
                  if (fieldResult) {
                    draftAssessment.results.fieldResults[fieldId] = fieldResult;
                  } else {
                    delete draftAssessment.results.fieldResults[fieldId];
                  }
                }
              });

              // Field Instances - merge into assessment
              union(
                Object.keys(assessment.fieldInstances),
                Object.keys(stateAssessment.results.fieldInstances),
              ).forEach((fieldInstanceId) => {
                const rawFieldInstance =
                  assessment.fieldInstances[fieldInstanceId];
                const fieldInstance = rawFieldInstance
                  ? offlineAssetFieldInstanceToAssessment(rawFieldInstance)
                  : undefined;
                if (
                  !equal(
                    stateAssessment.results.fieldInstances[fieldInstanceId],
                    fieldInstance,
                  )
                ) {
                  affectedFieldInstanceIds.push(fieldInstanceId);
                  if (fieldInstance) {
                    draftAssessment.results.fieldInstances[fieldInstanceId] =
                      fieldInstance;
                  } else {
                    delete draftAssessment.results.fieldInstances[
                      fieldInstanceId
                    ];
                  }
                }
              });

              // @todo merge actions into assessment - limited to reference at the moment
              draftAssessment.results.actions = Object.values(
                assessment.actions,
              ).reduce((acc, remote) => {
                if (remote?.reference) {
                  const local = acc[remote.id];
                  if (local && !local.reference) {
                    local.reference = remote.reference;
                  }
                }
                return acc;
              }, draftAssessment.results.actions);

              // @todo merge controls into assessment - limited to reference at the moment
              draftAssessment.results.controls = Object.values(
                assessment.controls,
              ).reduce((acc, remote) => {
                if (remote?.reference) {
                  const local = acc[remote.id];
                  if (local && !local.reference) {
                    local.reference = remote.reference;
                  }
                }
                return acc;
              }, draftAssessment.results.controls);

              // @todo merge property info, questions, checklists, signatories, actions, controls
            }
          }
        }

        if (affectedFieldIds.length) {
          const affectedFieldIdsUniq = uniq(affectedFieldIds);
          Object.values(stateAssessment.otherData.fields.groups)
            .filter(
              (group) =>
                !!stateAssessment.template.fields.groups[group.id] &&
                !group.repeatable &&
                !!intersection(affectedFieldIdsUniq, group.fieldIds).length,
            )
            .forEach((group) => {
              draftAssessment.stats.fieldGroupRequiredResultsCount[group.id] =
                getFieldGroupRequiredResultsValuesCountFromValues({
                  existingCount:
                    stateAssessment.stats.fieldGroupRequiredResultsCount[
                      group.id
                    ] ?? 0,
                  newCount: getFieldGroupRequiredValuesCount({
                    assetResults: draftAssessment.assetData.fieldResults,
                    results: draftAssessment.results.fieldResults,
                    group,
                    fields: stateAssessment.otherData.fields.fields,
                  }),
                  groupCount:
                    draftAssessment.stats.fieldGroupRequiredResultsCount[
                      group.id
                    ] ?? 0,
                });
            });
        }

        uniq(affectedFieldInstanceIds).forEach((id) => {
          const groupId =
            draftAssessment.assetData.fieldInstances[id]?.groupId ??
            draftAssessment.results.fieldInstances[id]?.groupId;
          if (!groupId) {
            return;
          }
          // Update stats for instance
          draftAssessment.stats.fieldInstanceRequiredResultsCount[id] =
            getFieldInstanceRequiredValuesCount({
              assetResults:
                draftAssessment.assetData.fieldInstances[id]?.results || {},
              instance: draftAssessment.results.fieldInstances[id],
              group: stateAssessment.otherData.fields.groups[groupId]!,
              fields: stateAssessment.otherData.fields.fields,
            });
          // Update stats for instance group
          draftAssessment.stats.fieldGroupRequiredResultsCount[groupId] =
            getFieldGroupRequiredResultsValuesCountFromValues({
              existingCount:
                stateAssessment.stats.fieldInstanceRequiredResultsCount[id] ??
                0,
              newCount:
                draftAssessment.stats.fieldInstanceRequiredResultsCount[id]!,
              groupCount:
                draftAssessment.stats.fieldGroupRequiredResultsCount[groupId] ??
                0,
            });
        });

        /**
         * Don't update approved actions - this won't actually change often but data
         * will be provided after every change made to the open assessment
         */
        // if (action.payload.updatedKeys.includes('actions')) {
        //   draftAssessment.assetData.actions = Object.values(
        //     action.payload.data.actions,
        //   );
        // }
        /**
         * Don't update approved controls - this won't actually change often but data
         * will be provided after every change made to the open assessment
         */
        // if (action.payload.updatedKeys.includes('controls')) {
        //   draftAssessment.assetData.controls = Object.values(
        //     action.payload.data.controls,
        //   );
        // }
      }
    }),
  )
  .handleAction(doAseLoadDataFromNewApi, (state, { payload: { response } }) => {
    const otherDataFields: Assessment['otherData']['fields'] = {
      sectionIds: response.organisation.fields.sectionIds,
      sections: mapObjectValues(
        response.organisation.fields.sections,
        (section): AseTplFieldSection => ({
          id: section.id,
          name: section.name,
          groupIds: section.groupIds,
        }),
      ),
      groups: Object.values(response.organisation.fields.sections).reduce<
        Record<string, AseTplFieldGroup>
      >(
        (acc, section) => ({
          ...acc,
          ...mapObjectValues(
            section.groups,
            (group): AseTplFieldGroup => ({
              id: group.id,
              name: group.name,
              disableAutoId: group.disableAutoId,
              nameSingular: group.nameSingular || group.name,
              repeatable: group.repeatable,
              autoIdPrefix: group.autoIdPrefix,
              autoIdZeroFill: group.autoIdZeroFill,
              fieldIds: group.fieldIds,
              allowPdfDownload: group.allowPdfDownload,
              calculated: {
                fieldsHaveCriteria: Object.values(group.fields).some(
                  (field) => !!field.criteria.length,
                ),
                requiredFieldIds: Object.values(group.fieldIds).filter(
                  (fieldId) =>
                    response.template.structure.fields.fields[fieldId]?.require,
                ),
              },
            }),
          ),
        }),
        {},
      ),
      fields: Object.values(response.organisation.fields.sections).reduce<
        Record<string, AseTplFieldField>
      >(
        (acc, section) =>
          Object.values(section.groups).reduce(
            (acc1, group) => ({
              ...acc1,
              ...mapObjectValues(
                group.fields,
                (field): AseTplFieldField => field,
              ),
            }),
            acc,
          ),
        {},
      ),
    };

    const fieldInstanceRequiredResultsCount: Record<string, number> = {};
    const fieldGroupRequiredResultsCount: Record<string, number> = {};

    Object.values(response.results.fieldInstances).forEach((fieldInstance) => {
      const group = otherDataFields.groups[fieldInstance.groupId];
      if (group) {
        const count = getFieldInstanceRequiredValuesCount({
          assetResults:
            response.asset.fieldInstances[fieldInstance.instanceId]?.results ??
            {},
          instance: fieldInstance,
          group,
          fields: otherDataFields.fields,
        });
        fieldInstanceRequiredResultsCount[fieldInstance.instanceId] = count;
        fieldGroupRequiredResultsCount[fieldInstance.groupId] =
          (fieldGroupRequiredResultsCount[fieldInstance.groupId] ?? 0) + count;
      }
    });

    // Calculate required values for non-repeatable groups
    Object.values(otherDataFields.groups).forEach((group) => {
      if (!group.repeatable) {
        const count = getFieldGroupRequiredValuesCount({
          assetResults: response.asset.fieldResults,
          results: response.results.fieldResults,
          group,
          fields: otherDataFields.fields,
        });
        fieldGroupRequiredResultsCount[group.id] =
          (fieldGroupRequiredResultsCount[group.id] ?? 0) + count;
      }
    });

    return {
      ...state,
      isOpen: true,
      context: {
        context: AppContextEnum.remoteAssessment,
        assetId: response.asset.id,
        assessmentId: response.id,
      },
      assessment: {
        id: response.id,
        isOnline: true,
        assetId: response.asset.id,
        organisationId: response.organisation.id,
        showValidationErrors: false,
        filterGroups: {},
        tempData: {
          repeatables: {},
        },
        features: {
          edit: response.features.edit,
          editassetplans: response.features.editassetplans,
          approve: response.features.approve,
          unsubmit: response.features.unsubmit,
          unapprove: response.features.unapprove,
          downloadBundle: response.features.downloadBundle,
        },

        stats: {
          fieldGroupRequiredResultsCount,
          fieldInstanceRequiredResultsCount,
        },

        results: {
          id: response.id,
          reference: response.results.reference,
          editable: response.editable,
          statusSummaryLabel: response.results.statusSummaryLabel,
          details: {
            dueDate: response.results.dueDate,
            customReference: response.results.details.customReference,
            assessedBy: response.results.details.assessedBy,
            assessedDate: response.results.details.assessedDate,
            assessedTime: response.results.details.assessedTime,
            approvedBy: response.results.details.approvedBy,
            approvedDate: response.results.details.approvedDate,
            fileIds: response.results.details.fileIds,
            latestApproved: response.results.latestApproved,
            riskLikelihoodId: response.results.details.riskLikelihoodId,
            riskSeverityId: response.results.details.riskSeverityId,
            riskChangeReason: response.results.details.riskChangeReason,
            startDateType: response.results.details.startDateType,
            startDate: response.results.details.startDate,
            statusId: response.results.details.statusId,
            expiryDateType: response.results.details.expiryDateType,
            expiryDateTimescale: response.results.details.expiryDateTimescale,
            expiryDate: response.results.details.expiryDate,
            actionTimescaleDate: response.results.details.actionTimescaleDate,
            actionTimescaleUpdateReviewed:
              response.results.details.actionTimescaleUpdateReviewed,
            remarksAssessor: response.results.details.remarksAssessor,
            remarksScope: response.results.details.remarksScope,
            remarksScopeChange: response.results.details.remarksScopeChange,
            riskReason: response.results.details.riskReason,
            risk: response.results.details.risk,
            authorOrganisation: response.results.details.authorOrganisation,
          },
          asset: {
            name: response.results.asset.name,
            customReference: response.results.asset.customReference,
            address1: response.results.asset.address1,
            address2: response.results.asset.address2,
            city: response.results.asset.city,
            county: response.results.asset.county,
            postcode: response.results.asset.postcode,
            avatar: response.results.asset.avatar,
            phone: response.results.asset.phone,
          },
          fieldResults: Object.entries(response.results.fieldResults).reduce<
            Record<string, FieldResult>
          >((acc, [_, item]) => {
            acc[_] = translateFieldResultNullable(item);
            return acc;
          }, {}),
          fieldInstances: Object.entries(
            response.results.fieldInstances,
          ).reduce<Record<string, AseFieldInstance>>((acc, [_, instance]) => {
            acc[_] = translateFieldRepeatableResultNullable(instance);
            return acc;
          }, {}),
          sections: Object.entries(response.results.sections).reduce<
            Record<string, SectionResult>
          >((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              comment: item.comment,
            };
            return acc;
          }, {}),
          questions: Object.entries(response.results.questions).reduce<
            Record<string, QuestionResult>
          >((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              answerId: item.answerId,
              comment: item.comment,
            };
            return acc;
          }, {}),
          questionComments: response.results.questionComments,
          checklistItems: Object.entries(
            response.results.checklistItems,
          ).reduce<Record<string, ChecklistItemResult>>((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              result: item.result,
            };
            return acc;
          }, {}),
          actions: Object.entries(response.results.actions).reduce<
            Record<string, Action>
          >((acc, [_, item]) => {
            acc[_] = translateAction(item);
            return acc;
          }, {}),
          controls: Object.entries(response.results.controls).reduce<
            Record<string, Control>
          >((acc, [_, item]) => {
            acc[_] = translateControl(item);
            return acc;
          }, {}),
          fieldInstanceChecks: response.results.fieldInstanceChecks,
          signatories: response.results.signatories,
          flags: response.results.flags,
          flagHistory: response.results.flagHistory,
        },
        template: {
          id: response.template.id,
          name: response.template.name,
          title: response.template.title,
          description: response.template.description,
          complianceTypeId: response.template.complianceTypeId,
          complianceSubtypeId: response.template.complianceSubtypeId,
          deleted: response.template.deleted,
          expiry_date_timescale: response.template.settings.expiryDateTimescale,
          requireAllAnswers: response.template.settings.requireAllAnswers,
          criteria: response.templateData.criteria,
          specification_conforms_to:
            response.template.settings.specificationConformsTo,
          default_remarks_scope: response.template.settings.defaultRemarksScope,
          default_remarks_scope_change:
            response.template.settings.defaultRemarksScopeChange,
          hidePreviousFindings: response.template.settings.hidePreviousFindings,
          previousActionsLimitComplianceTypes:
            response.template.settings.previousActionsLimitComplianceTypes,
          previousControlsLimitComplianceTypes:
            response.template.settings.previousControlsLimitComplianceTypes,
          settings: {
            editAssetName: response.template.settings.editAssetName,
            editAssetAddress: response.template.settings.editAssetAddress,
            editAssetAvatar: response.template.settings.editAssetAvatar,
            editAssetCustomReference:
              response.template.settings.editAssetCustomReference,
            questionComments: response.template.settings.questionComments,
            addCustomActions: response.template.settings.addCustomActions,
            addCustomControls: response.template.settings.addCustomControls,
          },
          section_ids: response.template.structure.sectionIds,
          sections: Object.entries(response.templateData.sections).reduce<
            Record<string, Section>
          >((acc, [sectionId, item]) => {
            acc[sectionId] = {
              id: item.id,
              name: item.name,
              description: item.description,
              question_ids:
                response.template.structure.sectionQuestions[sectionId] || [],
            };
            return acc;
          }, {}),
          questions: Object.entries(response.templateData.questions).reduce<
            Record<string, Question>
          >((acc, [questionId, item]) => {
            acc[questionId] = {
              id: item.id,
              question: item.question,
              criteria_id: item.criteriaId,
              level: item.level,
              parent_answer_id: Object.entries(
                response.template.structure.answerQuestions,
              ).reduce<string | undefined>(
                (acc1, [aId, qIds]) => (qIds.includes(item.id) ? aId : acc1),
                undefined,
              ),
              description: item.description,
              type: item.type as unknown as Question['type'],
              answer_ids:
                response.template.structure.questionAnswers[questionId] || [],
              checklistItemIds: (
                response.template.structure.questionChecklistItems[
                  questionId
                ] || []
              ).map(toInteger),
            };
            return acc;
          }, {}),
          checklistItems: Object.entries(
            response.templateData.checklistItems,
          ).reduce<Record<string, ChecklistItem>>((acc, [_, item]) => {
            acc[_] = {
              id: toInteger(item.id),
              text: item.text,
            };
            return acc;
          }, {}),
          answers: Object.entries(response.templateData.answers).reduce<
            Record<string, Answer>
          >((acc, [answerId, item]) => {
            acc[answerId] = {
              id: item.id,
              answer: item.answer,
              description: item.description,
              negative: item.negative,
              parent_question_id: Object.entries(
                response.template.structure.questionAnswers,
              ).reduce<string>(
                (acc1, [qId, aIds]) => (aIds.includes(item.id) ? qId : acc1),
                '-1',
              ),
              question_ids:
                response.template.structure.answerQuestions[answerId] || [],
            };
            return acc;
          }, {}),
          actionSuggestions: Object.entries(
            response.templateData.suggestedActions,
          ).reduce<Record<string, ActionSuggestion>>((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              description: item.description,
              why: item.why,
              timescaleId: item.timescaleId,
              severityId: item.severityId,
              categoryId: item.categoryId,
              dueDeterminedBy: item.dueDeterminedBy,
              dueTimescale: item.dueTimescale,
              dueTypeId: item.dueTypeId,
              positiveChecklistItemIds: item.positiveChecklistItemIds,
              negativeChecklistItemIds: item.negativeChecklistItemIds,
              answerIds: item.answerIds,
              cost: item.cost,
            };
            return acc;
          }, {}),
          controlSuggestions: Object.entries(
            response.templateData.suggestedControls,
          ).reduce<Record<string, ControlSuggestion>>((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              why: item.why,
              categoryId: item.categoryId,
              description: item.description,
              positiveChecklistItemIds: item.positiveChecklistItemIds,
              negativeChecklistItemIds: item.negativeChecklistItemIds,
              questionIds: item.questionIds,
            };
            return acc;
          }, {}),
          fieldInstanceChecks: response.templateData.fieldInstanceChecks,
          fields: {
            groups: mapObjectValues(
              response.template.structure.fields.groups,
              (
                _,
                groupId,
              ): Assessment['template']['fields']['groups'][string] => ({
                questionIds:
                  response.template.structure.fieldGroupQuestions[groupId] ||
                  [],
                checkIds:
                  response.template.structure.fieldGroupChecks[groupId] || [],
              }),
            ),
            fields: mapObjectValues(
              response.template.structure.fields.fields,
              (item): Assessment['template']['fields']['fields'][string] => ({
                edit: item.edit,
                require: item.require,
              }),
            ),
          },
          flags: response.templateData.flags,
        },
        task: response.task,
        assetData: {
          assessmentTimestamps: response.asset.assessmentTimestamps,
          riskRatings: response.asset.riskRatings,
          details: {
            name: response.asset.details.name,
            parentName: response.asset.details.parentName,
            reference: response.asset.details.reference,
            customReference: response.asset.details.customReference,
            address1: response.asset.details.address1,
            address2: response.asset.details.address2,
            city: response.asset.details.city,
            county: response.asset.details.county,
            postcode: response.asset.details.postcode,
            phone: response.asset.details.phone,
            avatar: response.asset.details.avatar,
          },
          files: Object.entries(response.asset.files).reduce<
            Record<string, File>
          >((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              name: item.name,
              type: item.type,
              timestamp: item.timestamp,
              editable: item.editable,
              deletable: item.deletable,
            };
            return acc;
          }, {}),
          actions: Object.values(response.asset.actions).map(translateAction),
          controls: Object.values(response.asset.controls).map(
            translateControl,
          ),
          fieldResults: Object.entries(response.asset.fieldResults).reduce<
            Record<string, FieldResult>
          >((acc, [_, item]) => {
            acc[_] = translateFieldResult(item);
            return acc;
          }, {}),
          fieldInstances: Object.entries(response.asset.fieldInstances).reduce<
            Record<string, AseFieldInstance>
          >((acc, [_, item]) => {
            acc[_] = translateFieldRepeatableResultNullable(item);
            return acc;
          }, {}),
          fieldInstanceAutoIds: response.asset.fieldInstanceAutoIds,
          defaultInstanceChecks: response.asset.defaultInstanceChecks || {},
          answers: response.asset.answers,
          teamResponsibility: {},
          plans: response.asset.plans,
        },
        otherData: {
          allowedAuthorOrganisations: response.allowedAuthorOrganisations,
          allowedAuthors: response.allowedAuthors,
          assessment_statuses: response.organisation.assessmentStatuses,
          accounts: response.organisation.accounts,
          due_types: response.organisation.dueTypes,
          priorities: response.organisation.timescales,
          secondary_priorities: response.organisation.severities,
          teams: Object.entries(response.organisation.teams).reduce<
            Record<string, Team>
          >((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              name: item.name,
              account_ids: item.accountIds,
            };
            return acc;
          }, {}),
          categories: Object.entries(response.organisation.categories).reduce<
            Record<string, Category>
          >((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              name: item.name,
              group_name: item.groupName,
              responsible_team_ids: item.responsibleTeamIds,
            };
            return acc;
          }, {}),
          compliance_sub_types: Object.entries(
            response.organisation.complianceSubtypes,
          ).reduce<Record<string, ComplianceSubtype>>((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              name: item.name,
              compliance_type_id: item.complianceTypeId,
            };
            return acc;
          }, {}),
          signatories: response.templateData.signatories,
          compliance_types: Object.entries(
            response.organisation.complianceTypes,
          ).reduce<Record<string, ComplianceType>>((acc, [_, item]) => {
            acc[_] = {
              id: item.id,
              name: item.name,
              matrix: item.matrix
                ? {
                    likelihood_ids: item.matrix.likelihoodIds,
                    severity_ids: item.matrix.severityIds,
                    outcomes: item.matrix.outcomes,
                    likelihoods: item.matrix.likelihoods,
                    severities: item.matrix.severities,
                    ratings: Object.fromEntries(
                      Object.entries(item.matrix.ratings).map(
                        ([ratingId, rating]): [
                          string,
                          Matrix['ratings'][string],
                        ] => [
                          ratingId,
                          {
                            id: rating.id,
                            name: rating.name,
                            bcolour: rating.colour,
                            tcolour: rating.colour,
                          },
                        ],
                      ),
                    ),
                  }
                : undefined,
            };
            return acc;
          }, {}),
          control_statuses: sortBy(
            Object.values(response.organisation.controlStatuses),
            'ordering',
          ).map((item) => ({
            id: item.id,
            name: item.name,
          })),
          task_statuses: sortBy(
            Object.values(response.organisation.taskStatuses),
            'ordering',
          ).map((item) => ({
            id: item.id,
            name: item.name,
          })),
          fields: otherDataFields,
        },
      },
    };
  })
  .handleAction(actions.doAseShowValidationErrors, (state, action) =>
    produce(state, (draft) => {
      if (draft.assessment) {
        draft.assessment.showValidationErrors = action.payload;
      }
    }),
  )
  .handleAction(actions.doAseAddTempRepeatables, (state, action) =>
    produce(state, (draft) => {
      if (draft.assessment && Object.keys(action.payload).length) {
        draft.assessment.tempData.repeatables = {
          ...draft.assessment.tempData.repeatables,
          ...action.payload,
        };
      }
    }),
  )

  // Other
  .handleAction(RESET_DEV_DATA, () => initialState)
  .handleAction(SIGN_OUT.success, () => initialState);
